/**
 * Token negotiation protocol — standardized message types for peer-to-peer
 * token request, grant, renewal, and revocation.
 *
 * Used by GeoCamera (sender) and CameraMap (receiver) over both direct
 * peer data channels and the Acequia event bus.
 */

// ---------------------------------------------------------------------------
// Message type constants
// ---------------------------------------------------------------------------

export const TOKEN_PRESENT = 'tokenPresent'
export const ACCESS_REQUEST = 'accessRequest'
export const ACCESS_RESPONSE = 'accessResponse'
export const TOKEN_RENEWED = 'tokenRenewal'
export const TOKEN_REVOKED = 'accessRevoked'
// A signed revocation object, handed peer to peer.
//
// Deliberately NOT an extension of accessRevoked above. That message is an
// unsigned assertion, and it is safe only because of what its receiver does
// with it — drop a token from its own wallet, so a forged one costs the
// receiver access it already had. Point that at chains and the incentive
// inverts: anyone could revoke anyone with one message. This type carries an
// object that proves its own authority instead, so the channel can stay dumb.
// Keeping them separate keeps that difference visible at the call site.
export const REVOCATION_SHARE = 'revocationShare'

// ---------------------------------------------------------------------------
// Builder functions
// ---------------------------------------------------------------------------

/**
 * Build a tokenPresent message — viewer presenting a stored token to a camera.
 * @param {object} opts
 * @param {string} opts.deviceId - Viewer's deviceId
 * @param {string} opts.userId - Viewer's userId
 * @param {string} opts.viewerId - Viewer's instanceId
 * @param {string} opts.token - JWT string
 * @param {string} [opts.tokenId] - CUID2 peer token id
 * @param {string[]} [opts.anchors] - the anchors an anchor-model grant hangs from
 *   (acequia.grants.grantPresentation), so a verifier that has never seen the grant
 *   can check it with no network
 * @returns {object}
 */
export function buildTokenPresent({ deviceId, userId, viewerId, token, tokenId, anchors }) {
  const msg = {
    type: TOKEN_PRESENT,
    deviceId,
    userId,
    viewerId,
    token,
    tokenId: tokenId || null,
  }
  if (Array.isArray(anchors) && anchors.length) msg.anchors = anchors
  return msg
}

/**
 * Build an accessRequest message — viewer requesting access from a camera.
 * @param {object} opts
 * @param {string} opts.deviceId - Viewer's deviceId
 * @param {string} opts.userId - Viewer's userId
 * @param {string} opts.viewerId - Viewer's instanceId
 * @param {string} opts.viewerName - Display name
 * @param {string} [opts.handle] - User handle (human-readable username)
 * @param {string} [opts.userDisplayName] - User's full display name (first/last)
 * @param {string} [opts.deviceName] - The viewer browser's device name (acequia's getDeviceName()):
 *   how the camera's owner tells a viewer that isn't signed in (no handle) from another
 * @param {string|object} [opts.scope='full'] - What is asked for: the granting app's preset
 *   word ('full', 'location', 'console-admin'), which that app maps to a grant set, or a
 *   grant set. The ask is a request in the app's vocabulary; the answer's scope is always
 *   a grant set
 * @param {string} [opts.aud] - The app asked ('geocamera', 'debug-console'). Lets each app's
 *   handler pick its own requests out of a shared channel without reading the scope
 *   (unified-token-system, 2026-09-23: the request carries the aud it asks for)
 * @returns {object}
 */
export function buildAccessRequest({ deviceId, userId, viewerId, viewerName, handle, userDisplayName, deviceName, scope = 'full', aud }) {
  const msg = {
    type: ACCESS_REQUEST,
    deviceId,
    userId,
    viewerId,
    viewerName,
    scope,
  }
  if (handle) msg.handle = handle
  if (userDisplayName) msg.userDisplayName = userDisplayName
  if (deviceName) msg.deviceName = deviceName
  if (aud) msg.aud = aud
  return msg
}

/**
 * Build an accessResponse message — camera granting or denying access.
 * @param {object} opts
 * @param {string} opts.cameraId - Camera's instanceId
 * @param {string} opts.cameraDeviceId - Camera's deviceId
 * @param {string} opts.cameraUserId - Camera's userId
 * @param {string} opts.cameraName - Camera display name
 * @param {boolean} opts.granted
 * @param {object} [opts.scope] - The granted scope (when granted): the grant set the token
 *   signs. Sent as given — it used to be coerced to 'full', relabelling a read-only grant
 *   as full access on the receiving side; from 2026-10-01 a preset word rode here with
 *   the set beside it as `grants`, and the 2026-11-02 sunset made it the set alone
 * @param {string} [opts.preset] - The granting app's name for that set ('view',
 *   'console-admin'): a label for the holder's record and UI, never read as a scope
 * @param {string} [opts.aud] - The app that granted ('geocamera', 'debug-console')
 * @param {string} [opts.token] - JWT (when granted)
 * @param {string} [opts.tokenId] - CUID2 id (when granted)
 * @param {number} [opts.expiresAt] - Timestamp ms (when granted)
 * @param {string} [opts.reason] - Denial reason (when not granted)
 * @param {string} [opts.message] - Human-readable denial message
 * @returns {object}
 */
export function buildAccessResponse({
  cameraId, cameraDeviceId, cameraUserId, cameraName,
  granted, scope, preset, token, tokenId, expiresAt, reason, message, aud,
}) {
  const msg = {
    type: ACCESS_RESPONSE,
    cameraId,
    cameraDeviceId: cameraDeviceId || null,
    cameraUserId: cameraUserId || null,
    cameraName,
    granted,
  }
  if (granted) {
    if (scope) msg.scope = scope
    if (preset) msg.preset = preset
    if (token) msg.token = token
    if (tokenId) msg.tokenId = tokenId
    if (expiresAt) msg.expiresAt = expiresAt
  } else {
    if (reason) msg.reason = reason
    if (message) msg.message = message
  }
  if (aud) msg.aud = aud
  return msg
}

/**
 * Build a tokenRenewal message — camera notifying viewer of renewed token.
 * @param {object} opts
 * @param {string} opts.token - New JWT
 * @param {string} opts.tokenId - CUID2 id (stable)
 * @param {number} opts.expiresAt - New expiry timestamp ms
 * @param {string} opts.cameraDeviceId
 * @param {string} opts.cameraUserId
 * @param {string} opts.cameraName
 * @returns {object}
 */
export function buildTokenRenewal({ token, tokenId, expiresAt, cameraDeviceId, cameraUserId, cameraName }) {
  return {
    type: TOKEN_RENEWED,
    token,
    tokenId,
    expiresAt,
    cameraDeviceId,
    cameraUserId,
    cameraName,
  }
}

/**
 * Build an accessRevoked message — camera notifying viewer of revocation.
 * @param {object} opts
 * @param {string} opts.cameraDeviceId
 * @param {string} opts.cameraUserId
 * @param {string} opts.cameraName
 * @param {string} [opts.tokenId] - Revoked token id
 * @returns {object}
 */
export function buildTokenRevocation({ cameraDeviceId, cameraUserId, cameraName, tokenId }) {
  const msg = {
    type: TOKEN_REVOKED,
    cameraDeviceId,
    cameraUserId,
    cameraName,
  }
  if (tokenId) msg.tokenId = tokenId
  return msg
}

// ---------------------------------------------------------------------------
// Handler registration helper
// ---------------------------------------------------------------------------

/**
 * Register handlers for token protocol messages on a peer data channel.
 * Dispatches incoming dataBig messages to the appropriate handler.
 *
 * @param {object} peer - Acequia peer with 'dataBig' events
 * @param {object} handlers
 * @param {function} [handlers.onTokenPresent] - (peer, data) => void
 * @param {function} [handlers.onAccessRequest] - (peer, data) => void
 * @param {function} [handlers.onAccessResponse] - (peer, data) => void
 * @param {function} [handlers.onTokenRenewed] - (peer, data) => void
 * @param {function} [handlers.onTokenRevoked] - (peer, data) => void
 * @param {function} [handlers.onOther] - (peer, data) => void — non-protocol messages
 * @returns {function} Listener function (for removal if needed)
 */
export function handleTokenMessages(peer, handlers = {}) {
  const listener = (data) => {
    switch (data.type) {
      case TOKEN_PRESENT:
        handlers.onTokenPresent?.(peer, data)
        break
      case ACCESS_REQUEST:
        handlers.onAccessRequest?.(peer, data)
        break
      case ACCESS_RESPONSE:
        handlers.onAccessResponse?.(peer, data)
        break
      case TOKEN_RENEWED:
        handlers.onTokenRenewed?.(peer, data)
        break
      case TOKEN_REVOKED:
        handlers.onTokenRevoked?.(peer, data)
        break
      default:
        handlers.onOther?.(peer, data)
    }
  }
  peer.on('dataBig', listener)
  return listener
}

/**
 * Build a revocationShare message — one signed revocation object, offered to a
 * peer that has already shown us the chain it belongs to.
 *
 * The sender is not named and does not matter. A revocation is checked, never
 * trusted, so who handed it over carries no weight — which is exactly what lets
 * this be accepted from anyone.
 *
 * @param {object} opts
 * @param {string} opts.jws - compact JWS, `typ: "revocation"`
 * @returns {object}
 */
export function buildRevocationShare({ jws }) {
  return { type: REVOCATION_SHARE, jws }
}
